import { useEffect, useState } from "react";
import { FileText, FileSpreadsheet } from "lucide-react";
import { appApi, authedFileUrl, inr, fmtDate, fmtDateTime } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";

export default function Invoices() {
  const [data, setData] = useState(null);

  useEffect(() => {
    appApi.get("invoices").then(setData).catch(() => setData({ invoices: [] }));
  }, []);

  const invoices = data?.invoices || [];

  return (
    <div data-testid="invoices-page" className="space-y-6">
      <div>
        <p className="text-xs font-mono font-bold uppercase tracking-[0.2em] text-blaze">Billing</p>
        <h1 className="font-display font-black tracking-tight text-3xl sm:text-4xl mt-1">Invoices</h1>
        <p className="text-sm text-zinc-500 mt-2">GST invoices raised by ShipZilla for your shipments — download as PDF or Excel.</p>
      </div>

      <div className="border-2 border-obsidian bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]" data-testid="invoices-table">
          <thead>
            <tr className="border-b-2 border-obsidian text-left">
              {["Invoice", "Period", "Orders", "Taxable", "GST", "Total", "Status", "Download"].map((h) => (
                <th key={h} className="px-4 py-3 text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {invoices.map((v, i) => (
              <tr key={v.id || i} className="border-b border-zinc-100 last:border-0 hover:bg-acid/10 transition-colors duration-100" data-testid={`invoice-row-${v.id || i}`}>
                <td className="px-4 py-3">
                  <p className="font-mono text-xs font-bold flex items-center gap-2">
                    <FileText className="w-4 h-4 text-acid" /> {v.number || v.invoice_no || v.id}
                  </p>
                  <p className="text-[10px] font-mono text-zinc-400 mt-1">raised {fmtDateTime(v.created_at)}</p>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-zinc-500">
                  {v.start && v.end ? `${fmtDate(v.start)} → ${fmtDate(v.end)}` : v.period || "—"}
                </td>
                <td className="px-4 py-3 font-mono text-xs">{v.orders ?? "—"}</td>
                <td className="px-4 py-3 font-mono text-xs">{v.taxable != null ? inr(v.taxable) : "—"}</td>
                <td className="px-4 py-3 font-mono text-xs">{v.gst != null ? inr(v.gst) : "—"}</td>
                <td className="px-4 py-3 font-mono text-xs font-bold" data-testid={`invoice-total-${v.id || i}`}>{v.total != null ? inr(v.total) : "—"}</td>
                <td className="px-4 py-3">
                  <StatusBadge status={v.status || (v.paid ? "paid" : "unpaid")} />
                  {v.paid_at && <p className="text-[10px] font-mono text-green-700 mt-1">Paid on {fmtDate(v.paid_at)}</p>}
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <a href={authedFileUrl(`invoices/${v.id}/pdf`)} target="_blank" rel="noreferrer" data-testid={`invoice-pdf-${v.id || i}`}
                      className="inline-flex items-center gap-1.5 text-[11px] font-mono font-bold uppercase tracking-wider px-3 py-1.5 border-2 border-obsidian bg-acid text-white hover:bg-obsidian transition-colors duration-150">
                      <FileText className="w-3.5 h-3.5" /> PDF
                    </a>
                    <a href={authedFileUrl(`invoices/${v.id}/xlsx`)} target="_blank" rel="noreferrer" data-testid={`invoice-xlsx-${v.id || i}`}
                      className="inline-flex items-center gap-1.5 text-[11px] font-mono font-bold uppercase tracking-wider px-3 py-1.5 border-2 border-obsidian bg-white hover:bg-acid hover:text-white transition-colors duration-150">
                      <FileSpreadsheet className="w-3.5 h-3.5" /> Excel
                    </a>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data && invoices.length === 0 && (
          <p className="px-5 py-10 text-sm text-zinc-500" data-testid="invoices-empty">No invoices yet — they appear here once ShipZilla bills your shipments.</p>
        )}
        {!data && <p className="px-5 py-10 font-mono text-xs uppercase tracking-[0.3em] text-zinc-400">Loading invoices…</p>}
      </div>
    </div>
  );
}
